import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { colors } from '../../theme/tokens';
import useReducedMotion from '../../hooks/useReducedMotion';

const ConfettiContainer = styled('div')({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  zIndex: 9999,
  overflow: 'hidden',
});

const ConfettiPiece = styled('div')(({ delay, duration, startX, color, reducedMotion }) => ({
  position: 'absolute',
  top: '-10px',
  left: `${startX}%`,
  width: '10px',
  height: '10px',
  backgroundColor: color,
  opacity: reducedMotion ? 0 : 1,
  animation: reducedMotion ? 'none' : `confetti-fall ${duration}s ${delay}s ease-in forwards`,
  transformOrigin: 'center',

  '@keyframes confetti-fall': {
    '0%': {
      transform: 'translateY(0) rotate(0deg) scale(1)',
      opacity: 1,
    },
    '100%': {
      transform: `translateY(100vh) rotate(${Math.random() * 720 - 360}deg) scale(0.5)`,
      opacity: 0,
    },
  },
}));

/**
 * Confetti celebration effect component
 * Shows colorful confetti falling from the top of the screen
 * Respects prefers-reduced-motion accessibility setting
 *
 * @param {Object} props
 * @param {boolean} props.active - Whether to show confetti
 * @param {number} props.duration - How long the effect lasts (milliseconds)
 * @param {number} props.pieceCount - Number of confetti pieces
 * @param {Function} props.onComplete - Callback when animation completes
 */
export default function ConfettiCelebration({
  active = false,
  duration = 3000,
  pieceCount = 50,
  onComplete,
}) {
  const reducedMotion = useReducedMotion();
  const [show, setShow] = useState(false);

  const confettiColors = [
    colors.primary.main,
    colors.secondary.main,
    colors.success.main,
    colors.warning.main,
    '#FFD700', // Gold
    '#FF69B4', // Pink
    '#00CED1', // Turquoise
    '#FF6347', // Tomato
  ];

  useEffect(() => {
    if (active && !reducedMotion) {
      setShow(true);

      // Auto-hide after duration
      const timer = setTimeout(() => {
        setShow(false);
        if (onComplete) onComplete();
      }, duration);

      return () => clearTimeout(timer);
    } else if (active && reducedMotion) {
      // If reduced motion, immediately call onComplete
      if (onComplete) onComplete();
    }
  }, [active, duration, onComplete, reducedMotion]);

  if (!show || reducedMotion) return null;

  // Generate random confetti pieces
  const confettiPieces = Array.from({ length: pieceCount }, (_, i) => ({
    id: i,
    delay: Math.random() * 0.5, // 0-500ms delay
    duration: 2 + Math.random() * 1, // 2-3s fall duration
    startX: Math.random() * 100, // Random horizontal position
    color: confettiColors[Math.floor(Math.random() * confettiColors.length)],
  }));

  return (
    <ConfettiContainer>
      {confettiPieces.map((piece) => (
        <ConfettiPiece
          key={piece.id}
          delay={piece.delay}
          duration={piece.duration}
          startX={piece.startX}
          color={piece.color}
          reducedMotion={reducedMotion}
        />
      ))}
    </ConfettiContainer>
  );
}
